import React, { useState, useEffect } from 'react';
import { actualizarStock } from '../api';

function statusClass(stock) {
  if (stock === 0) return { num: '#c62828', border: '#ffcdd2' };
  if (stock <= 4)  return { num: '#e67e22', border: '#ffcc80' };
  return { num: '#128C7E', border: '#c8e6c9' };
}

function statusTexto(stock) {
  if (stock === 0) return { texto: 'Agotado', bg: '#ffebee', color: '#c62828' };
  if (stock <= 4)  return { texto: 'Poco',    bg: '#fff3e0', color: '#e65100' };
  return { texto: 'OK',      bg: '#e8f5e9', color: '#2e7d32' };
}

const ORDEN_TALLAS = ['XS','S','M','L','XL','XXL','XXXL'];

function ordenarVariantes(variantes) {
  return [...variantes].sort((a, b) => {
    if (a.color < b.color) return -1;
    if (a.color > b.color) return 1;
    const tallaA = (a.talla || '').split(' / ').pop().trim();
    const tallaB = (b.talla || '').split(' / ').pop().trim();
    const iA = ORDEN_TALLAS.indexOf(tallaA.toUpperCase());
    const iB = ORDEN_TALLAS.indexOf(tallaB.toUpperCase());
    if (iA === -1 && iB === -1) return tallaA.localeCompare(tallaB);
    if (iA === -1) return 1;
    if (iB === -1) return -1;
    return iA - iB;
  });
}

function Inventario({ onCambiosChange }) {
  const [data,      setData]      = useState([]);
  const [filtro,    setFiltro]    = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cambios,   setCambios]   = useState({});
  const [cargando,  setCargando]  = useState(true);

  useEffect(() => {
    cargarProductos();
  }, []);

  // Interceptar navegación si hay cambios pendientes
  useEffect(() => {
    function handleBeforeUnload(e) {
      if (Object.keys(cambios).length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cambios]);

  async function cargarProductos() {
    setCargando(true);
    const res = await fetch('https://api.pixventa.com/productos');
    const productos = await res.json();
    setData(productos);
    setCargando(false);
  }

  function cambiarStock(varianteId, delta, stockActual) {
    const nuevoStock = Math.max(0, stockActual + delta);
    setData(prev => prev.map(prod => ({
      ...prod,
      variantes: prod.variantes ? prod.variantes.map(v =>
        v.id === varianteId ? { ...v, stock: nuevoStock } : v
      ) : []
    })));
    setCambios(prev => { const n = { ...prev, [varianteId]: nuevoStock }; onCambiosChange && onCambiosChange(Object.keys(n).length > 0); return n; });
  }

  function setStock(varianteId, valor) {
    const nuevoStock = Math.max(0, parseInt(valor) || 0);
    setData(prev => prev.map(prod => ({
      ...prod,
      variantes: prod.variantes ? prod.variantes.map(v =>
        v.id === varianteId ? { ...v, stock: nuevoStock } : v
      ) : []
    })));
    setCambios(prev => { const n = { ...prev, [varianteId]: nuevoStock }; onCambiosChange && onCambiosChange(Object.keys(n).length > 0); return n; });
  }

  async function guardar() {
    setGuardando(true);
    await Promise.all(
      Object.entries(cambios).map(([varianteId, stock]) =>
        actualizarStock(varianteId, stock)
      )
    );
    setCambios({});
    onCambiosChange && onCambiosChange(false);
    setGuardando(false);
    alert('✅ Inventario guardado');
  }

  const todasVariantes = data.flatMap(p => p.variantes || []);
  const totalPiezas    = todasVariantes.reduce((a, v) => a + (v.stock || 0), 0);
  const totalVariantes = todasVariantes.length;
  const stockBajo      = todasVariantes.filter(v => v.stock > 0 && v.stock <= 4).length;
  const agotados       = todasVariantes.filter(v => v.stock === 0).length;

  const filtrados = data.filter(p =>
    p.nombre.toLowerCase().includes(filtro.toLowerCase())
  );

  if (cargando) return <div style={styles.loading}>Cargando inventario...</div>;

  return (
    <div style={styles.container}>

      <div style={styles.summary}>
        <div style={styles.sumItem}><div style={styles.sumVal}>{totalPiezas}</div><div style={styles.sumLbl}>Total piezas</div></div>
        <div style={styles.sumItem}><div style={styles.sumVal}>{totalVariantes}</div><div style={styles.sumLbl}>Variantes</div></div>
        <div style={styles.sumItem}><div style={{ ...styles.sumVal, color: '#e67e22' }}>{stockBajo}</div><div style={styles.sumLbl}>Stock bajo</div></div>
        <div style={styles.sumItem}><div style={{ ...styles.sumVal, color: '#c62828' }}>{agotados}</div><div style={styles.sumLbl}>Agotados</div></div>
      </div>

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="🔍 Buscar producto..."
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
        />
        <button style={{ ...styles.saveBtn, opacity: guardando ? 0.7 : 1 }} onClick={guardar} disabled={guardando}>
          {guardando ? '⏳' : 'Guardar'}
        </button>
      </div>

      <div style={styles.leyenda}>
        <span style={styles.legItem}><span style={{ ...styles.legDot, background: '#25D366' }} />OK (5+)</span>
        <span style={styles.legItem}><span style={{ ...styles.legDot, background: '#e67e22' }} />Poco (1-4)</span>
        <span style={styles.legItem}><span style={{ ...styles.legDot, background: '#c62828' }} />Agotado</span>
        {Object.keys(cambios).length > 0 && (
          <span style={styles.unsaved}>● {Object.keys(cambios).length} cambios sin guardar</span>
        )}
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '40%' }}>Producto / Variante</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Stock</th>
              <th style={styles.th}>Precio</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((prod) => (
              <React.Fragment key={prod.id}>
                <tr style={styles.rowGroup}>
                  <td colSpan={4} style={styles.tdGroup}>
                    <div style={styles.groupCell}>
                      <span>{prod.emoji || '📦'}</span>
                      <span>{prod.nombre}</span>
                      <span style={styles.totalBadge}>
                        {(prod.variantes || []).reduce((a, v) => a + (v.stock || 0), 0)} piezas
                      </span>
                    </div>
                  </td>
                </tr>
                {ordenarVariantes(prod.variantes || []).map((v) => {
                  const sc = statusClass(v.stock || 0);
                  const st = statusTexto(v.stock || 0);
                  return (
                    <tr key={v.id} style={styles.rowVariant}>
                      <td style={styles.tdVariant}>
                        <div style={styles.varLabel}>
                          <span style={{ ...styles.colorDot, background: v.color_hex || '#ccc' }} />
                          {v.talla} · {v.color}
                        </div>
                      </td>
                      <td style={styles.tdCenter}>
                        <div style={{ ...styles.stockWrap, borderColor: sc.border }}>
                          <button style={styles.stockBtn} onClick={() => cambiarStock(v.id, -1, v.stock || 0)}>−</button>
                          <input
                            style={{ ...styles.stockNum, color: sc.num }}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min="0"
                            value={v.stock || 0}
                            onFocus={e => e.target.select()}
                            onChange={e => setStock(v.id, e.target.value)}
                          />
                          <button style={styles.stockBtn} onClick={() => cambiarStock(v.id, 1, v.stock || 0)}>+</button>
                        </div>
                      </td>
                      <td style={styles.tdPrecio}>${prod.precio}</td>
                      <td style={styles.tdCenter}>
                        <span style={{ ...styles.pill, background: st.bg, color: st.color }}>
                          {st.texto}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

const styles = {
  container:   { padding: 16, paddingBottom: 90 },
  loading:     { padding: 40, textAlign: 'center', color: '#7A8F89' },
  toolbar:     { display: 'flex', gap: 8, marginBottom: 10 },
  search:      { flex: 1, background: 'white', border: '1.5px solid #E4EDE8', borderRadius: 10, padding: '9px 12px', fontSize: 13, outline: 'none' },
  saveBtn:     { background: '#25D366', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  leyenda:     { display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' },
  legItem:     { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#7A8F89' },
  legDot:      { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  unsaved:     { fontSize: 11, color: '#856404', background: '#fff3cd', borderRadius: 20, padding: '2px 10px', fontWeight: 600 },
  tableWrap:   { background: 'white', borderRadius: 16, border: '1px solid #E4EDE8', overflow: 'hidden', overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 380 },
  th:          { padding: '11px 10px', textAlign: 'left', background: '#075E54', color: 'white', fontSize: 11, fontWeight: 700 },
  rowGroup:    { background: 'linear-gradient(to right, #f0fff5, #f8fcf9)' },
  tdGroup:     { padding: '9px 10px' },
  groupCell:   { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, color: '#0D1F1A' },
  totalBadge:  { marginLeft: 'auto', background: '#F0F4F0', color: '#7A8F89', borderRadius: 20, padding: '2px 10px', fontSize: 10, fontWeight: 600 },
  rowVariant:  { borderBottom: '1px solid #E4EDE8' },
  tdVariant:   { padding: '7px 10px 7px 24px' },
  varLabel:    { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7A8F89' },
  colorDot:    { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, border: '1px solid rgba(0,0,0,0.1)' },
  tdCenter:    { padding: '7px 10px', textAlign: 'center', verticalAlign: 'middle' },
  tdPrecio:    { padding: '7px 10px', fontSize: 13, fontWeight: 600, color: '#128C7E' },
  stockWrap:   { display: 'inline-flex', alignItems: 'center', border: '1.5px solid', borderRadius: 8, overflow: 'hidden', width: 110 },
  stockBtn:    { width: 28, height: 32, background: '#F0F4F0', border: 'none', fontSize: 16, fontWeight: 700, color: '#128C7E', cursor: 'pointer', flexShrink: 0 },
  stockNum:    { flex: 1, border: 'none', outline: 'none', textAlign: 'center', fontSize: 14, fontWeight: 700, background: 'white', width: 0, minWidth: 0, padding: 0, MozAppearance: 'textfield', WebkitAppearance: 'none' },
  pill:        { display: 'inline-block', padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700 },
  summary:     { background: 'white', borderRadius: 14, padding: '12px 16px', marginBottom: 12, border: '1px solid #E4EDE8', display: 'flex' },
  sumItem:     { flex: 1, textAlign: 'center', borderRight: '1px solid #E4EDE8', padding: '0 6px' },
  sumVal:      { fontSize: 17, fontWeight: 800, color: '#0D1F1A' },
  sumLbl:      { fontSize: 10, color: '#7A8F89', marginTop: 2 },
};

export default Inventario;